import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_ITEMS, ASSETS } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header data-testid="main-navbar" className="fixed top-0 inset-x-0 z-50 pt-3 sm:pt-4 px-3 sm:px-6 pointer-events-none">
      <div
        className={`pointer-events-auto mx-auto max-w-7xl rounded-[22px] transition-all duration-500 ${
          scrolled || open
            ? "glass shadow-[0_20px_50px_-30px_rgba(14,27,49,0.35)]"
            : "bg-white/40 backdrop-blur-md border border-white/60"
        }`}
      >
        <div className="flex items-center justify-between h-[68px] px-4 sm:px-6">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src={ASSETS.logo} alt="CRESTIA — Growth & Transformation" className="h-10 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Κύρια πλοήγηση">
            {NAV_ITEMS.filter((i) => i.id !== "contact").map((item) => (
              <NavLink
                key={item.id}
                to={item.path}
                data-testid={`nav-link-${item.id}`}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-[13px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#0E1B31] font-semibold bg-[#0E1B31]/[0.06]" : "text-[#3B4A5E] hover:text-[#0E1B31] hover:bg-[#0E1B31]/[0.04]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className="ml-3 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-[#0E1B31] text-[#F8F6F0] text-[13px] font-semibold tracking-wide hover:bg-[#C26D52] transition-colors duration-300"
            >
              Επικοινωνία
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#0E1B31] h-10 w-10 rounded-full flex items-center justify-center hover:bg-[#0E1B31]/[0.06]"
            aria-label={open ? "Κλείσιμο μενού" : "Άνοιγμα μενού"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid="nav-mobile-menu"
              className="lg:hidden overflow-hidden border-t border-[#0E1B31]/10"
              aria-label="Πλοήγηση κινητού"
            >
              <div className="px-6 py-7 flex flex-col gap-5">
                {NAV_ITEMS.map((item, idx) => (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    data-testid={`nav-mobile-link-${item.id}`}
                    className={({ isActive }) => `font-display text-2xl font-medium ${isActive ? "text-[#C26D52]" : "text-[#0E1B31]"}`}
                  >
                    <span className="font-mono-brand text-xs text-[#C26D52] mr-3">0{idx + 1}</span>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
